import { Component, h, Prop, State, Host, Watch } from '@stencil/core';
import { translations, markets } from './pn-product-pricelist-translations';
import { FindPriceWeight, PnfindPriceMarket, PnfindPricei18n, FindPriceSizeCategory } from '../pn-find-price/pn-find-price-types';
import { ProductPriceSourceData, ProductPriceItem } from '../../../globals/shared/productprice/product-price-types';
import { ProductPriceListi18n, ProductPriceListMarket, ProductPriceListWeight } from './pn-product-pricelist-types';
import { weights } from './data.js';
import { convertFileFromStream } from '../../../globals/shared/productprice/convert.js';
// import { state } from "./pn-product-pricelist-store";
import { state } from '../pn-find-price/pn-find-price-store';

@Component({
  tag: 'pn-product-pricelist',
  styleUrl: 'pn-product-pricelist.scss',
})
export class PnFindProductPricelist {
  @Prop() source: string = 'https://com-inte.postnord.com/globalassets/global/prices/sv-findprice-1-PN.xlsx';
  @Prop({ mutable: true }) language: string = 'sv';
  @Prop({ mutable: true }) market: string = 'se';
  @Prop() productid: string = '';

  @State() filteredItems: ProductPriceItem[] = [];
  @State() sourceData: ProductPriceSourceData = { types: [], data: [] };
  @State() gotData: boolean = false;
  @State() loading: boolean = false;

  @State() postagetype: any = { value: '' };
  @State() weight: ProductPriceListWeight = { value: 0, name: '' } as ProductPriceListWeight;
  @State() weightvalue: number = 0;

  componentWillLoad() {
    this.setState();
    this.getDataSource().then(() => {});
  }

  @Watch('market')
  @Watch('language')
  setState() {
    if (this.market) {
      state.market = markets[this.market] as ProductPriceListMarket;
    }
    if (this.language && translations[this.language]) {
      state.i18n = translations[this.language] as PnfindPricei18n;
    }
  }

  async getDataSource() {
    if (!this.productid) {
      return;
    }
    this.loading = true;
    const cacheBusting = `?cache=${Math.floor(Math.random() * (10000 - 1) + 1)}`;
    const req = await fetch(this.source + cacheBusting);
    const reqbody = await req.blob();
    try {
      const convertedData = await convertFileFromStream(reqbody);
      this.sourceData = convertedData;
      this.filterItems();
    } catch (e) {
      this.loading = false;
      console.warn('Unable to parse data');
    }
  }

  @Watch('postagetype')
  @Watch('weightvalue')
  filterItems() {
    if (!this.sourceData?.data) {
      console.info('Data has not yet been loaded');
      return;
    }
    this.loading = false;
    const productIdItems = this.sourceData.data.filter(x => x.id === this.productid);
    if (productIdItems.length === 0) {
      return;
    }
    this.postagetype.value = productIdItems[0].what;

    if (!weights[this.postagetype.value] || weights[this.postagetype.value].length === 0) {
      return;
    }
    if (this.weightvalue === 0) {
      this.weight = weights[this.postagetype.value][0];
      this.weightvalue = this.weight.value;
    }

    // For letters we use grams. Otherwise we use KG
    const comparisonWeight = this.weight.abbreviation.toLocaleLowerCase() === 'kg' && this.postagetype.value === 'letter' ? this.weight.value * 1000 : this.weight.value;

    let weightFilteredData = productIdItems.filter(x => x.maxweight === comparisonWeight);

    // In parcels the maxwidths don't necessarily start at 1kg so we fetch any type below the user selected value
    if (weightFilteredData.length === 0) {
      weightFilteredData = productIdItems.filter(x => x.maxweight >= comparisonWeight);
      if (weightFilteredData.length > 0) {
        // We reverse the result array since the map will take the last unique item instead of the first
        const uniqueWeightOptions = [...new Map(weightFilteredData.reverse().map(item => [item['id'], item])).values()];
        if (uniqueWeightOptions.length > 0) {
          weightFilteredData = uniqueWeightOptions;
        }
      }
    }

    this.filteredItems = weightFilteredData;

    this.gotData = this.filteredItems.length > 0;
  }

  render() {
    return (
      <Host>
        <div class="content">
          <slot />
        </div>

        {this.loading ? <pn-spinner></pn-spinner> : null}

        {this.postagetype.value && weights[this.postagetype.value] ? (
          <div class="weightselection">
            <div class="weightoption">
              <div class="weightoption-input">
                <pn-input
                  inputid="weightvalue"
                  name="weightvalue"
                  type="number"
                  label={state.i18n.weightlabel}
                  value={this.weightvalue}
                  onChange={e => {
                    if (e && e.target && e.target.value) {
                      const weightNum = parseInt(e.target.value, 10);
                      if (!isNaN(weightNum)) {
                        let matchingWeights = (weights[this.postagetype.value] as ProductPriceListWeight[]).filter(x => {
                          let weightInGrams = x.abbreviation === 'kg' ? x.value * 1000 : x.value;
                          return weightInGrams >= weightNum;
                        });
                        if (matchingWeights && matchingWeights.length > 0) {
                          this.weight = matchingWeights[0];
                          this.weightvalue = this.weight.value;
                        }
                      }
                    }
                  }}
                ></pn-input>
                <span class="weightoption-input-abbreviation">{this.weight.abbreviation}</span>
              </div>
              <div class="weightoption-range">
                <input
                  type="range"
                  id="weightrange"
                  name="weightrange"
                  list="weightlist"
                  value={!this.weight.value ? 0 : weights[this.postagetype.value].findIndex(x => x.value === this.weightvalue)}
                  onChange={e => {
                    const value = (e.target as HTMLInputElement).value;
                    this.weight = weights[this.postagetype.value][value];
                    this.weightvalue = this.weight.value;
                  }}
                  min="0"
                  // max={ weights[this.postagetype.value][weights[this.postagetype.value].length - 1].value }
                  max={weights[this.postagetype.value].length - 1}
                />
                <datalist id="weightlist">
                  {weights[this.postagetype.value].map((weight, weightindex) => {
                    let labelText = weight.name;
                    if (weights[this.postagetype.value].length > 6) {
                      labelText = weightindex % 5 === 0 || weightindex === 0 || weightindex === weights[this.postagetype.value].length - 1 ? weight.name : '';
                    }
                    return <option value={weightindex} {...(labelText ? { label: labelText } : {})}></option>;
                  })}
                </datalist>
              </div>
            </div>
          </div>
        ) : null}

        <div class="resultlist" data-list-count={this.filteredItems.length}>
          {this.filteredItems.map((item: ProductPriceItem) => (
            <div>
              {/* <pn-product-pricelist-result item={item}></pn-product-pricelist-result> */}
              <pn-find-price-result item={item}></pn-find-price-result>
            </div>
          ))}
        </div>
      </Host>
    );
  }
}
